/**
 * PostHog analytics client for questionnaire flow tracking.
 * No-op when REACT_APP_POSTHOG_KEY is not set.
 */

import posthog from 'posthog-js';

declare const process: {
  env: {
    REACT_APP_POSTHOG_KEY?: string;
    REACT_APP_POSTHOG_HOST?: string;
    [key: string]: string | undefined;
  };
};

let initialized = false;

function initOnce(): boolean {
  if (initialized) return true;
  const key = process.env.REACT_APP_POSTHOG_KEY;
  if (!key?.trim()) return false;
  try {
    posthog.init(key, {
      api_host: process.env.REACT_APP_POSTHOG_HOST || 'https://app.posthog.com',
      person_profiles: 'identified_only',
    });
    initialized = true;
    return true;
  } catch {
    return false;
  }
}

/**
 * Get the PostHog client for PostHogProvider. Returns null if not configured.
 * Call once at app root and pass to PostHogProvider; capture/group use the same instance.
 */
export function getPostHogClient(): typeof posthog | null {
  return initOnce() ? posthog : null;
}

/**
 * Capture a custom event. No-op if PostHog is not configured.
 */
export function capture(event: string, properties?: Record<string, unknown>): void {
  if (initOnce()) {
    posthog.capture(event, properties);
  }
}

/**
 * Set group for this distinct_id (e.g. flow_session). No-op if PostHog is not configured.
 */
export function group(groupType: string, groupKey: string, groupProperties?: Record<string, unknown>): void {
  if (initOnce()) {
    posthog.group(groupType, groupKey, groupProperties);
  }
}

/**
 * Identify user (e.g. after login). No-op if PostHog is not configured.
 */
export function identify(distinctId: string, personProperties?: Record<string, unknown>): void {
  if (initOnce()) {
    posthog.identify(distinctId, personProperties);
  }
}

/**
 * Build a non-PII payload for tax_calculation_received from backend tax_calculation object.
 */
export function buildTaxCalculationPayload(
  taxCalculation: Record<string, unknown>,
  sessionId: string,
  country: string | null,
  taxYear: number
): Record<string, unknown> {
  const base = {
    session_id: sessionId,
    country: country ?? undefined,
    tax_year: taxYear,
    calculation_successful: !!taxCalculation.calculation_successful,
  };

  if (taxCalculation.calculation_successful) {
    const breakdown = (taxCalculation.breakdown as Record<string, unknown>) || {};
    return {
      ...base,
      calculation_id: taxCalculation.calculation_id,
      total_tax_liability: typeof taxCalculation.total_tax_liability === 'number' ? taxCalculation.total_tax_liability : undefined,
      effective_tax_rate: typeof taxCalculation.effective_tax_rate === 'number' ? taxCalculation.effective_tax_rate : undefined,
      marginal_tax_rate: typeof taxCalculation.marginal_tax_rate === 'number' ? taxCalculation.marginal_tax_rate : undefined,
      tax_regime: typeof taxCalculation.tax_regime === 'string' ? taxCalculation.tax_regime : undefined,
      total_income: typeof breakdown.total_income === 'number' ? breakdown.total_income : undefined,
      taxable_income: typeof breakdown.taxable_income === 'number' ? breakdown.taxable_income : undefined,
      total_tax_credits: typeof breakdown.total_tax_credits === 'number' ? breakdown.total_tax_credits : undefined,
      income_tax_before_credits: typeof breakdown.income_tax_before_credits === 'number' ? breakdown.income_tax_before_credits : undefined,
      income_tax_after_credits: typeof breakdown.income_tax_after_credits === 'number' ? breakdown.income_tax_after_credits : undefined,
      autonomous_community: typeof taxCalculation.autonomous_community === 'string' ? taxCalculation.autonomous_community : undefined,
    };
  }

  const msg = typeof taxCalculation.message === 'string'
    ? taxCalculation.message
    : typeof taxCalculation.error === 'string'
      ? taxCalculation.error.split('\n')[0].slice(0, 200)
      : 'Tax calculation failed';
  return {
    ...base,
    error_type: 'exception',
    message: msg,
  };
}
